import { create } from 'zustand';
import { getAddressTypes, getStates } from '../services/listsApi';
import { getAccountInfo, putAccountInfo } from '../services/accountApi';
import {
  getSessionUser,
  setSessionUser,
  getIsImpersonating,
  type SessionUser,
} from '../utils/session';
import type {
  PhoneNumberUpdateEntry,
  StreetAddressUpdateEntry,
  AddressTypeOption,
} from '../types/api';

// ─── Local types ──────────────────────────────────────────────────────────────

export interface PersonalInfoStateOption {
  StateAbbr: string;
  StateName: string;
}

// ─── Required-field validation (VSDP-5988) ───────────────────────────────────
// Only the currently visible/selected phone and address entries are validated —
// other stored phone/address types not shown in the form are left to the
// existing stripping behavior below.

function isSelectedPhoneValid(phone: PhoneNumberUpdateEntry | undefined): boolean {
  if (!phone) {
    return true;
  }
  return phone.AreaCode.trim() !== '' && phone.PhoneNumber.trim() !== '';
}

function isSelectedAddressValid(address: StreetAddressUpdateEntry | undefined): boolean {
  if (!address) {
    return true;
  }
  return (
    address.Street1.trim() !== '' &&
    address.City.trim() !== '' &&
    address.State.trim() !== '' &&
    address.Zip.trim() !== ''
  );
}

// ─── Store interface ──────────────────────────────────────────────────────────

export interface PersonalInfoStore {
  // Read-only display fields (from session — not editable)
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  emailAddress: string;

  // Editable phone numbers (mirrors session user's PhoneNumbers array)
  phoneNumbers: PhoneNumberUpdateEntry[];
  selectedPhoneTypeId: number | null;

  // Editable addresses (mirrors session user's StreetAddresses array)
  addresses: StreetAddressUpdateEntry[];
  selectedAddressTypeId: number | null;

  // Address type options from API
  addressTypes: AddressTypeOption[];

  // State options for address form
  stateOptions: PersonalInfoStateOption[];

  // Form state
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  successMessage: string | null;

  // JSON snapshot for dirty check
  originalData: string | null;

  // Actions
  loadFromSession: () => void;
  loadAddressTypes: () => Promise<void>;
  loadStates: () => Promise<void>;
  setPhoneNumbers: (phones: PhoneNumberUpdateEntry[]) => void;
  updatePhoneEntry: (typeId: number, field: 'AreaCode' | 'PhoneNumber', value: string) => void;
  setAddresses: (addresses: StreetAddressUpdateEntry[]) => void;
  updateAddressField: (
    typeId: number,
    field: keyof Omit<StreetAddressUpdateEntry, 'AddressTypeId' | 'AddressTypeDescr'>,
    value: string
  ) => void;
  setSelectedPhoneTypeId: (id: number) => void;
  setSelectedAddressTypeId: (id: number) => void;
  savePersonalInfo: () => Promise<void>;
  clearMessages: () => void;
  isDirty: () => boolean;
  reset: () => void;
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  emailAddress: '',
  phoneNumbers: [] as PhoneNumberUpdateEntry[],
  selectedPhoneTypeId: null as number | null,
  addresses: [] as StreetAddressUpdateEntry[],
  selectedAddressTypeId: null as number | null,
  addressTypes: [] as AddressTypeOption[],
  stateOptions: [] as PersonalInfoStateOption[],
  isLoading: false,
  isSaving: false,
  error: null as string | null,
  successMessage: null as string | null,
  originalData: null as string | null,
};

// ─── Store ────────────────────────────────────────────────────────────────────

export const usePersonalInfoStore = create<PersonalInfoStore>((set, get) => ({
  ...initialState,

  loadFromSession: () => {
    const user = getSessionUser<SessionUser>();
    if (!user) return;

    const phones: PhoneNumberUpdateEntry[] = (
      (user.PhoneNumbers as Array<Record<string, unknown>> | undefined) ?? []
    ).map(p => ({
      PhoneTypeId: (p.PhoneTypeId as number) ?? 0,
      PhoneTypeDescr: (p.PhoneTypeDescr as string) ?? '',
      AreaCode: (p.AreaCode as string) ?? '',
      PhoneNumber: (p.PhoneNumber as string) ?? '',
    }));

    const addresses: StreetAddressUpdateEntry[] = (
      (user.StreetAddresses as Array<Record<string, unknown>> | undefined) ?? []
    ).map(a => ({
      AddressTypeId: (a.AddressTypeId as number) ?? 0,
      AddressTypeDescr: (a.AddressTypeDescr as string) ?? '',
      Street1: (a.Street1 as string) ?? '',
      Street2: (a.Street2 as string) ?? '',
      City: (a.City as string) ?? '',
      State: (a.State as string) ?? '',
      Zip: (a.Zip as string) ?? '',
    }));

    const originalData = JSON.stringify({ phones, addresses });

    set({
      firstName: (user.FirstName as string) ?? '',
      lastName: (user.LastName as string) ?? '',
      dateOfBirth: (user.DateOfBirth as string) ?? '',
      emailAddress: (user.EmailAddress as string) ?? '',
      phoneNumbers: phones,
      selectedPhoneTypeId: phones.length > 0 ? phones[0].PhoneTypeId : null,
      addresses,
      selectedAddressTypeId: addresses.length > 0 ? (addresses[0].AddressTypeId ?? null) : null,
      originalData,
    });
  },

  loadAddressTypes: async () => {
    set({ isLoading: true, error: null });
    try {
      const result = await getAddressTypes();
      if (result.StatusCode === 'SUCCESS' && Array.isArray(result.AddressTypes)) {
        const types: AddressTypeOption[] = result.AddressTypes.map(t => {
          // Normalize both AddressTypeID and AddressTypeId casings from backend
          const id = (t.AddressTypeID ?? t.AddressTypeId) as number | undefined;
          const name = t.AddressTypeName === 'Account' ? 'Home' : (t.AddressTypeName ?? '');
          return { AddressTypeID: id, AddressTypeId: id, AddressTypeName: name };
        });

        // Also update selected address type to the first from the API list
        const currentSelected = get().selectedAddressTypeId;
        const newSelected =
          currentSelected != null
            ? currentSelected
            : types.length > 0
              ? (types[0].AddressTypeID ?? types[0].AddressTypeId ?? null)
              : null;

        set({ addressTypes: types, selectedAddressTypeId: newSelected, isLoading: false });
      } else {
        set({ isLoading: false, error: 'Unable to load address types.' });
      }
    } catch {
      set({ isLoading: false, error: 'Unable to load address types.' });
    }
  },

  loadStates: async () => {
    // Try sessionStorage first (legacy pattern — BillingForm also stores/reads from here)
    const cached = window.sessionStorage.getItem('statesList');
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as PersonalInfoStateOption[];
        set({ stateOptions: parsed });
        return;
      } catch {
        /* ignore — fall through to API */
      }
    }

    try {
      const result = await getStates();
      if (result.StatusCode === 'SUCCESS' && Array.isArray(result.States)) {
        // Backend returns StateAbbr/StateName (type annotation uses Abbreviation/Name incorrectly)
        const states = result.States as unknown as PersonalInfoStateOption[];
        window.sessionStorage.setItem('statesList', JSON.stringify(states));
        set({ stateOptions: states });
      }
    } catch {
      set({ error: 'Unable to load state list.' });
    }
  },

  setPhoneNumbers: phones => set({ phoneNumbers: phones }),

  updatePhoneEntry: (typeId, field, value) => {
    const phones = get().phoneNumbers.map(p =>
      p.PhoneTypeId === typeId ? { ...p, [field]: value } : p
    );
    set({ phoneNumbers: phones });
  },

  setAddresses: addresses => set({ addresses }),

  updateAddressField: (typeId, field, value) => {
    const addresses = get().addresses.map(a =>
      a.AddressTypeId === typeId ? { ...a, [field]: value } : a
    );
    set({ addresses });
  },

  setSelectedPhoneTypeId: id => set({ selectedPhoneTypeId: id }),

  setSelectedAddressTypeId: id => {
    const { addresses, addressTypes } = get();
    if (addresses.some(a => a.AddressTypeId === id)) {
      set({ selectedAddressTypeId: id });
      return;
    }

    // Member has no saved address for this type yet (e.g. selecting "Vacation" for
    // the first time) — seed a blank entry so it renders and is editable/saveable,
    // instead of the detail fields silently disappearing.
    const typeOption = addressTypes.find(t => (t.AddressTypeID ?? t.AddressTypeId) === id);
    const blankEntry: StreetAddressUpdateEntry = {
      AddressTypeId: id,
      AddressTypeDescr: typeOption?.AddressTypeName ?? '',
      Street1: '',
      Street2: '',
      City: '',
      State: '',
      Zip: '',
    };
    set({ addresses: [...addresses, blankEntry], selectedAddressTypeId: id });
  },

  savePersonalInfo: async () => {
    if (getIsImpersonating()) {
      set({ error: 'You cannot save changes while impersonating another user.' });
      return;
    }

    const { selectedPhoneTypeId, selectedAddressTypeId, phoneNumbers, addresses } = get();
    const selectedPhone = phoneNumbers.find(p => p.PhoneTypeId === selectedPhoneTypeId);
    const selectedAddress = addresses.find(a => a.AddressTypeId === selectedAddressTypeId);

    if (!isSelectedPhoneValid(selectedPhone) || !isSelectedAddressValid(selectedAddress)) {
      set({
        error: 'Please complete all required fields before saving.',
        successMessage: null,
      });
      return;
    }

    set({ isSaving: true, error: null, successMessage: null });

    try {
      // Strip entries with empty AreaCode (mirrors legacy cleanup loop)
      const cleanedPhones = get().phoneNumbers.filter(
        p => p.AreaCode != null && p.AreaCode.trim() !== ''
      );

      // Strip entries with empty Street1 (mirrors legacy cleanup loop)
      const cleanedAddresses = get().addresses.filter(
        a => a.Street1 != null && a.Street1.trim() !== ''
      );

      const payload = {
        EmailAddress: get().emailAddress,
        FirstName: get().firstName,
        LastName: get().lastName,
        DateOfBirth: get().dateOfBirth,
        PhoneNumbers: cleanedPhones,
        StreetAddresses: cleanedAddresses,
        GenderId: 9,
        GenderName: 'Not Specified',
      };

      const result = await putAccountInfo(payload);

      if (result.StatusCode === 'SUCCESS') {
        // Refresh the sessionStorage-cached user so a later remount's loadFromSession()
        // reads the just-saved values instead of the stale pre-save snapshot (mirrors the
        // same fetch-then-cache pattern useBillingStore uses after a credit card save).
        const updatedUser = await getAccountInfo();
        setSessionUser(updatedUser);

        const newSnapshot = JSON.stringify({
          phones: get().phoneNumbers,
          addresses: get().addresses,
        });
        set({
          isSaving: false,
          successMessage: 'You have successfully updated your personal information.',
          originalData: newSnapshot,
        });
      } else {
        set({
          isSaving: false,
          error: result.StatusMessage ?? 'An error occurred while updating your information.',
        });
      }
    } catch {
      set({ isSaving: false, error: 'An error occurred while updating your information.' });
    }
  },

  clearMessages: () => set({ error: null, successMessage: null }),

  isDirty: () => {
    const { phoneNumbers, addresses, originalData } = get();
    if (originalData === null) {
      return false;
    }
    return JSON.stringify({ phones: phoneNumbers, addresses }) !== originalData;
  },

  reset: () => set({ ...initialState }),
}));
